import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchData } from "../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { ChevronsRightIcon } from "lucide-react";
import { AddButton, PaginationDropdown } from "../common/GeneralComponent";
import { Pagination } from "@mui/material";
import Loader from "../partials/Loader";
import {
  Download,
} from "lucide-react";
import GeneralFooter from "../common/GeneralFooter";
import PermissionButton from "../common/PermissionButton";
import * as XLSX from "xlsx";

const ProposedFeaturesList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const hasDataToExport = villages.length > 0;

  const fetchVillages = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      params.append("page", pagination.currentPage);
      params.append("limit", pagination.pageSize);

      const response = await fetchData(`proposed-features/villages?${params.toString()}`, "GET");

      // Sort so newest (highest ID or latest created_at) is on top
      const sorted = (response.data || []).sort((a, b) => {
        // If you have a created_at field, prefer this:
        // return new Date(b.created_at) - new Date(a.created_at);

        // Otherwise, use village_id (assuming higher = newer)
        return b.village_id - a.village_id;
      });

      setVillages(sorted);

      // Calculate pagination
      const totalItems = sorted.length;
      const totalPages = Math.ceil(totalItems / pagination.pageSize);
      setPagination((prev) => ({
        ...prev,
        totalItems,
        totalPages,
      }));
    } catch (error) {
      toast.error("Failed to load villages");
      setVillages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllVillagesForExport = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("search", debouncedSearch);
      params.append("page", 1);
      params.append("limit", 10000);

      const response = await fetchData(`proposed-features/villages?${params.toString()}`, "GET");

      return response?.data || [];
    } catch (error) {
      console.error("Error fetching all villages:", error);
      return [];
    }
  };

  const fetchDetailedBoqsForVillage = async (villageId) => {
    try {
      const response = await fetchData(`proposed-features/boqs/${villageId}`, "GET");
      return response?.data || [];
    } catch (error) {
      console.error(`Error fetching BOQs for village ${villageId}:`, error);
      return [];
    }
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      let allVillages = await fetchAllVillagesForExport();

      if (allVillages.length === 0) {
        toast.warning("No villages found with current filters.");
        setIsExporting(false);
        return;
      }

      allVillages.sort((a, b) => b.village_id - a.village_id);

      const villageIds = allVillages.map((v) => v.village_id);
      const detailedBoqsByVillage = {};
      for (const villageId of villageIds) {
        detailedBoqsByVillage[villageId] = await fetchDetailedBoqsForVillage(villageId);
      }

      // Fetch features map
      const featuresRes = await fetchData("features", "GET");
      const featuresMap = new Map(featuresRes?.data?.map(f => [f.feature_id, f.feature_name]) || []);

      // Collect all boqs for unique ids
      const allBoqs = allVillages.flatMap(v => detailedBoqsByVillage[v.village_id] || []);

      // Unique feature_ids for subtypes
      const uniqueFeatureIds = [...new Set(allBoqs.map(b => b.feature_id).filter(id => id))];
      const subtypesMap = new Map();
      for (const featId of uniqueFeatureIds) {
        try {
          const res = await fetchData(`feature-subtypes-by-feature/${featId}`, "GET");
          res?.data?.forEach(s => {
            subtypesMap.set(s.feature_sub_type_id, s.feature_sub_type);
          });
        } catch (e) {
          console.error("Error fetching subtypes:", e);
        }
      }

      // Unique subtype_ids for categories
      const uniqueSubtypeIds = [...new Set(allBoqs.map(b => b.feature_sub_type_id).filter(id => id))];
      const categoriesMap = new Map();
      for (const subId of uniqueSubtypeIds) {
        try {
          const res = await fetchData(`feature-categories-fetch-by-subtype/${subId}`, "GET");
          res?.data?.forEach(c => {
            categoriesMap.set(c.feature_category_id, c.category_name);
          });
        } catch (e) {
          console.error("Error fetching categories:", e);
        }
      }

      // Unique category_ids for designs
      const uniqueCategoryIds = [...new Set(allBoqs.map(b => b.feature_category_id).filter(id => id))];
      const designsByCategory = new Map();
      for (const catId of uniqueCategoryIds) {
        try {
          const res = await fetchData(`feature-designs/${catId}`, "GET");
          const designMap = new Map(res?.data?.map(d => [d.feature_category_designs_id, d.design_name]) || []);
          designsByCategory.set(catId, designMap);
        } catch (e) {
          console.error("Error fetching designs:", e);
        }
      }

      const workbook = XLSX.utils.book_new();

      const getConstructionType = (type) => {
        switch (type) {
          case "rehab":
            return "Rehab";
          case "new":
            return "New";
          default:
            return type || "N/A";
        }
      };

      const getHouseholdDisplay = (households_details, households) => {
        if (households_details && households_details.length > 0) {
          return households_details
            .map((hh) => `${hh.household_number} - ${hh.hh_head_name}`)
            .join(", ");
        }
        return `${households?.length || 0} household(s)`;
      };

      const basicKeys = [
        "Village ID",
        "Village Name",
        "Feature Count",
      ];

      const featureFieldBases = [
        "#",
        "Proposed Code",
        "Feature Name",
        "Sub Type",
        "Category Name",
        "Design Name",
        "Construction Type",
        "Cost (PKR)",
        "Latitude",
        "Longitude",
        "Households Served",
        "BOQ File Type",
        "Details",
      ];

      const allRows = [];

      allVillages.forEach((village) => {
        const boqs = detailedBoqsByVillage[village.village_id] || [];
        const numFeatures = boqs.length;
        const row = {
          "Village ID": village.village_id || "N/A",
          "Village Name": village.village_name || "N/A",
          "Feature Count": numFeatures,
        };

        // feature-specific columns
        boqs.forEach((boq, featureIndex) => {
          const featureNum = featureIndex + 1;
          featureFieldBases.forEach((field) => {
            let value = "N/A";
            switch (field) {
              case "#":
                value = featureNum;
                break;
              case "Proposed Code":
                value = boq.proposed_code || "N/A";
                break;
              case "Feature Name":
                value = boq.feature?.feature_name || featuresMap.get(boq.feature_id) || "N/A";
                break;
              case "Sub Type":
                value = subtypesMap.get(boq.feature_sub_type_id) || boq.feature_sub_type || "N/A";
                break;
              case "Category Name":
                value = boq.FeaturesCategory?.category_name || boq.feature_category?.category_name || categoriesMap.get(boq.feature_category_id) || "N/A";
                break;
              case "Design Name":
                value = designsByCategory.get(boq.feature_category_id)?.get(boq.feature_category_designs_id) || boq.feature_category_design || "N/A";
                break;
              case "Construction Type":
                value = getConstructionType(boq.construction_type);
                break;
              case "Cost (PKR)":
                value = boq.cost ? boq.cost.toLocaleString() : "N/A";
                break;
              case "Latitude":
                value = boq.latitude || "N/A";
                break;
              case "Longitude":
                value = boq.longitude || "N/A";
                break;
              case "Households Served":
                value = getHouseholdDisplay(boq.households_details, boq.households);
                break;
              case "BOQ File Type":
                if (boq.boq_file_path) {
                  value = "Custom";
                } else if (boq.default_boq_file) {
                  value = "Default";
                } else {
                  value = "None";
                }
                break;
              case "Details":
                value = boq.details || "N/A";
                break;
            }
            row[`Feature ${featureNum} ${field === "#" ? "#" : field}`] = value;
          });
        });

        allRows.push(row);
      });

      // sorted columns
      const maxFeatures = Math.max(
        ...allVillages.map((village) => (detailedBoqsByVillage[village.village_id] || []).length)
      );

      let sortedKeys = [...basicKeys];
      let headerMapping = {};

      // basic keys with same display name
      basicKeys.forEach((key) => {
        headerMapping[key] = key;
      });

      for (let f = 1; f <= maxFeatures; f++) {
        featureFieldBases.forEach((field) => {
          const fullKey = `Feature ${f} ${field === "#" ? "#" : field}`;

          if (allRows.some((r) => r[fullKey] !== undefined)) {
            sortedKeys.push(fullKey);
            const displayHeader = field === "#" ? "Feature #" : field;
            headerMapping[fullKey] = displayHeader;
          }
        });
      }

      // array of arrays for worksheet
      const headers = sortedKeys.map((key) => headerMapping[key]);
      const data = allRows.map((row) =>
        sortedKeys.map((key) => row[key] ?? "")
      );
      const aoa = [headers, ...data];

      // worksheet
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      for (let colNum = 0; colNum < headers.length; colNum++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colNum });
        if (!ws[cellAddress]) continue;
        if (!ws[cellAddress].s) ws[cellAddress].s = {};
        ws[cellAddress].s.font = { size: 10, bold: true };
        ws[cellAddress].s.alignment = {
          horizontal: "center",
          vertical: "center",
        };
      }

      for (let rowNum = 1; rowNum <= data.length; rowNum++) {
        for (let colNum = 0; colNum < headers.length; colNum++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
          if (!ws[cellAddress]) continue;

          if (!ws[cellAddress].s) ws[cellAddress].s = {};

          ws[cellAddress].s.font = { size: 9 };

          ws[cellAddress].s.alignment = {
            horizontal: "left",
            wrapText: true,
            vertical: "center",
          };

          ws[cellAddress].t = "s";
        }
      }

      ws["!cols"] = headers.map(() => ({ wch: 15 }));

      // sheet to workbook
      XLSX.utils.book_append_sheet(workbook, ws, "Proposed Features Data");

      // filename
      let filename = "proposed_features_export";

      // date to filename
      const currentDate = new Date().toISOString().split("T")[0];
      filename += `_${currentDate}.xlsx`;

      // Write file
      XLSX.writeFile(workbook, filename);

      toast.success(`Export completed successfully! File: ${filename}`);
    } catch (error) {
      console.error("Error during export:", error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (search === debouncedSearch) return;
      setDebouncedSearch(search);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchVillages();
  }, [debouncedSearch, pagination.currentPage, pagination.pageSize]);

  // Filter villages based on search
  const filteredVillages = villages.filter(
    (village) =>
      village.village_name?.toLowerCase().includes(search.toLowerCase()) ||
      village.boq_count?.toString().includes(search)
  );

  // Paginate filtered results
  const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
  const paginatedVillages = filteredVillages.slice(
    startIndex,
    startIndex + pagination.pageSize
  );

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
      totalPages: Math.ceil(filteredVillages.length / size),
    }));
  };

  const columns = [
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Sr.No</span>
      ),
      cell: (row, index) => <span>{startIndex + index + 1}</span>,
      width: "80px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Village</span>
      ),
      selector: (row) => row.village_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Feature Count
        </span>
      ),
      selector: (row) => row.boq_count || 0,
      sortable: true,
      center: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Action</span>
      ),
      cell: (row) => (
        <button
          onClick={() =>
            navigate(`/proposed-features/manage/${row.village_id}`)
          }
          className="bg-[#137e81] hover:bg-[#0f6b6e] text-white px-3 py-1 rounded-md font-semibold transition-colors duration-200"
        >
          Manage
        </button>
      ),
      center: true,
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  useEffect(() => {
    const totalPages = Math.ceil(filteredVillages.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalPages,
      totalItems: filteredVillages.length,
    }));
  }, [filteredVillages.length, pagination.pageSize]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="w-full px-4">
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Proposed Feature | Infrastructure
          </h1>
          <div className="flex items-center gap-3">
            {(!loading && hasDataToExport) && (
              <button
                onClick={handleExport}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg font-semibold transition-all ${
                  isExporting
                    ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                    : "bg-[#137e81] text-white hover:bg-[#0f6c6f] hover:scale-105"
                }`}
                title="Export filtered data to Excel"
              >
                <Download size={18} />
                {isExporting ? "Exporting..." : "Export Excel"}
              </button>
            )}
            <PermissionButton
              hasPermission={permissions.includes(
                "infrastructure-information-create"
              )}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg my-[30px] shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Proposed BOQs
          </h1>
          {search && (
            <div className="text-sm text-gray-600">
              {pagination.totalItems} records found
              {hasDataToExport && (
                <span className="ml-2 text-green-600 font-medium">
                  • Export available
                </span>
              )}
            </div>
          )}
        </div>

        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              placeholder="Search villages..."
              type="search"
              className="font-[Light] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
            />
          </div>

          <DataTable
            columns={columns}
            persistTableHead
            data={paginatedVillages}
            customStyles={customStyles}
            highlightOnHover
            progressPending={false}
          />

          <GeneralFooter
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            dataLength={paginatedVillages.length}
          />
        </div>
      </div>
    </div>
  );
};

export default ProposedFeaturesList;